import { useRef, useCallback, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { MonthMarker } from '../utils/timelineUtils';
import { CloseIcon, PlayIcon, PauseIcon } from './icons';

interface TimelineControlProps {
  progress: number;
  onProgressChange: (progress: number) => void;
  isPlaying: boolean;
  onPlayPause: () => void;
  onClose: () => void;
  monthMarkers: MonthMarker[];
  currentDateLabel: string;
  isVisible: boolean;
}

export function TimelineControl({
  progress,
  onProgressChange,
  isPlaying,
  onPlayPause,
  onClose,
  monthMarkers,
  currentDateLabel,
  isVisible,
}: TimelineControlProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Auto-focus the slider when entering Time Machine mode
  useEffect(() => {
    if (isVisible) {
      // Slight delay to ensure the element is rendered and animated in
      const timer = setTimeout(() => {
        trackRef.current?.focus();
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Calculate progress from mouse/touch position
  const calculateProgress = useCallback((clientX: number) => {
    if (!trackRef.current) return 0;

    const rect = trackRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const newProgress = (x / rect.width) * 100;
    return Math.max(0, Math.min(100, newProgress));
  }, []);

  // Handle mouse/touch down on track
  const handleTrackPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const newProgress = calculateProgress(e.clientX);
    onProgressChange(newProgress);

    // Capture pointer for drag tracking (use trackRef for consistent behavior)
    trackRef.current?.setPointerCapture(e.pointerId);
  }, [calculateProgress, onProgressChange]);

  // Handle pointer move (dragging)
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const newProgress = calculateProgress(e.clientX);
    onProgressChange(newProgress);
  }, [isDragging, calculateProgress, onProgressChange]);

  // Handle pointer up (end drag)
  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    setIsDragging(false);
    trackRef.current?.releasePointerCapture(e.pointerId);
  }, []);

  // Handle keyboard navigation on slider
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    let delta = 0;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        delta = -5;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        delta = 5;
        break;
      case 'Home':
        onProgressChange(0);
        e.preventDefault();
        return;
      case 'End':
        onProgressChange(100);
        e.preventDefault();
        return;
      case ' ':
        onPlayPause();
        e.preventDefault();
        return;
      case 'Escape':
        onClose();
        e.preventDefault();
        return;
      default:
        return;
    }

    if (delta !== 0) {
      e.preventDefault();
      onProgressChange(Math.max(0, Math.min(100, progress + delta)));
    }
  }, [progress, onProgressChange, onPlayPause, onClose]);

  // Mark odd markers for conditional hiding on mobile when there are many
  const markersWithVisibility = monthMarkers.map((marker, index) => ({
    ...marker,
    hideOnMobile: monthMarkers.length > 8 && index % 2 === 1,
  }));

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-14 lg:bottom-16 left-0 right-0 z-[1500] bg-bg-surface border-t border-primary/50 shadow-lg shadow-primary/20"
        >
          <div className="px-4 py-3 lg:px-6 lg:py-4">
            {/* Control row */}
            <div className="flex items-center gap-3 lg:gap-4">
              {/* Close button */}
              <button
                type="button"
                onClick={onClose}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-bg-elevated hover:bg-bg-base text-text-secondary hover:text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                aria-label="Exit Time Machine mode"
              >
                <CloseIcon />
              </button>

              {/* Play/Pause button */}
              <button
                type="button"
                onClick={onPlayPause}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-primary hover:bg-primary/80 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <PauseIcon />
                ) : (
                  <PlayIcon className="w-5 h-5 ml-0.5" />
                )}
              </button>

              {/* Timeline track */}
              <div className="flex-1 relative">
                {/* Track container with touch area */}
                <div
                  ref={trackRef}
                  className="relative h-10 flex items-center cursor-pointer touch-none outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 focus:ring-offset-bg-surface rounded-lg"
                  onPointerDown={handleTrackPointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                  role="slider"
                  aria-label="Timeline position"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progress)}
                  aria-valuetext={currentDateLabel}
                  tabIndex={0}
                  onKeyDown={handleKeyDown}
                >
                  {/* Track background */}
                  <div className="absolute inset-x-0 h-1.5 bg-bg-elevated rounded-full">
                    {/* Progress fill */}
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary via-pink-500 to-emerald-500 rounded-full transition-[width] duration-75"
                      style={{ width: `${progress}%` }}
                    />
                  </div>

                  {/* Month markers */}
                  {markersWithVisibility.map((marker, index) => (
                    <div
                      key={`${marker.label}-${index}`}
                      className={`absolute top-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none ${
                        marker.hideOnMobile ? 'hidden lg:flex' : ''
                      }`}
                      style={{ left: `${marker.position}%` }}
                    >
                      <div className="w-0.5 h-3 bg-text-muted/30 rounded-full" />
                    </div>
                  ))}

                  {/* Draggable thumb */}
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-white rounded-full shadow-lg transition-transform timeline-thumb-glow ${
                      isDragging ? 'scale-125 shadow-xl' : 'hover:scale-110'
                    } ${isPlaying ? 'timeline-thumb-playing' : ''}`}
                    style={{ left: `${progress}%` }}
                  >
                    {/* Inner glow */}
                    <div className="absolute inset-1 bg-primary rounded-full" />
                  </div>
                </div>

                {/* Month labels row */}
                <div className="relative h-5 mt-1 select-none pointer-events-none">
                  {markersWithVisibility.map((marker, index) => (
                    <span
                      key={`label-${marker.label}-${index}`}
                      className={`absolute text-[10px] lg:text-xs text-text-muted -translate-x-1/2 whitespace-nowrap ${
                        marker.hideOnMobile ? 'hidden lg:inline' : ''
                      }`}
                      style={{ left: `${marker.position}%` }}
                    >
                      {marker.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Current date label (desktop only) */}
              <div className="hidden lg:block min-w-[120px] text-right">
                <span className="text-sm text-text-primary font-medium">
                  {currentDateLabel}
                </span>
              </div>
            </div>

            {/* Current date label (mobile only - centered below) */}
            <div className="lg:hidden mt-2 text-center">
              <span className="text-xs text-text-secondary">
                {currentDateLabel}
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
